import React from 'react';
import { AppBar, Toolbar, Box, Container, Typography, Divider, Chip } from '@mui/material';
import { Terminal, Code } from '@mui/icons-material';
import config from '../config';

interface AppLayoutProps {
  children: React.ReactNode;
  onLogoClick?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, onLogoClick }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Full-width header band - subtle blue gradient */}
      <Box
        sx={{
          width: '100%',
          background: 'linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.98) 100%)',
          borderBottom: '1px solid rgba(59,130,246,0.1)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: 'transparent',
            borderRadius: 0,
          }}
        >
          {/* Top accent line - subtle blue */}
          <Box
            sx={{
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.3) 50%, transparent 100%)',
            }}
          />

        <Toolbar sx={{ py: 1.5, minHeight: { xs: 64, sm: 72 }, px: { xs: 0, sm: 0 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexGrow: 1,
              width: '100%',
            }}
          >
            {/* Logo Section */}
            <Box
              onClick={onLogoClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flexShrink: 0,
                position: 'relative',
                height: '100%',
                minWidth: { xs: 180, sm: 220 },
                '&:hover': {
                  filter: 'drop-shadow(0 4px 12px rgba(245,158,11,0.25))',
                }
              }}
            >
              <Box
                component="img"
                src="/assets/logo.webp"
                alt="Cloud Ambassadors"
                sx={{
                  height: { xs: 48, sm: 56 },
                  width: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                  transform: 'scale(1.875)',
                  transformOrigin: 'left center',
                  filter: 'drop-shadow(0 2px 6px rgba(245,158,11,0.15))',
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.innerHTML = '<span style="font-size: 1.3rem; font-weight: 800; background: linear-gradient(90deg, #ffffff 0%, #fbbf24 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-family: \'Inter\', sans-serif; letter-spacing: -0.5px;">CLOUD AMBASSADORS</span>';
                  e.currentTarget.parentElement?.appendChild(fallback);
                }}
              />
            </Box>

            {/* Divider */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: 'rgba(148,163,184,0.3)',
                borderWidth: '1px',
                height: '48px',
                alignSelf: 'center',
              }}
            />

            {/* Portal Title - Now Clickable */}
            <Box
              onClick={onLogoClick}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateX(2px)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Terminal sx={{ fontSize: 16, color: '#3b82f6', opacity: 0.8 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    color: '#f1f5f9',
                    letterSpacing: '-0.3px',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  CA-Evaluate
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                  color: 'rgba(148,163,184,0.7)',
                  fontSize: '0.65rem',
                  letterSpacing: '0.3px',
                  display: { xs: 'none', sm: 'block' },
                  ml: 2.5,
                }}
              >
                Technical Evaluation Portal
              </Typography>
            </Box>
          </Box>

          {/* Status Indicator */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
            <Chip
              icon={<Code sx={{ fontSize: 14 }} />}
              label="ACTIVE"
              size="small"
              sx={{
                background: 'rgba(16,185,129,0.1)',
                border: '1px solid rgba(16,185,129,0.25)',
                color: '#10b981',
                fontFamily: '"Fira Code", monospace',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                height: '26px',
                '& .MuiChip-icon': {
                  color: '#10b981',
                }
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>
      </Box>

      <Container
        component="main"
        maxWidth="xl"
        sx={{
          flexGrow: 1,
          pt: 4,
          pb: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </Container>

      {/* Full-width footer band - subtle blue gradient */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          py: 2.5,
          px: 2,
          mt: 'auto',
          background: 'linear-gradient(0deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.98) 100%)',
          borderTop: '1px solid rgba(59,130,246,0.1)',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.3)',
          position: 'relative',
        }}
      >
        {/* Top accent line - subtle blue */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.3) 50%, transparent 100%)',
          }}
        />

        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            <Typography
              variant="body2"
              align="center"
              sx={{
                fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'center',
                color: '#f59e0b',
              }}
            >
              <Box component="span" sx={{ color: '#f59e0b', fontWeight: 600 }}>{'>'}</Box>
              <Box component="span">© {new Date().getFullYear()} {config.app.organization}</Box>
              <Box component="span" sx={{ color: 'rgba(245,158,11,0.4)' }}>|</Box>
              <Box component="span" sx={{ color: '#fbbf24' }}>{config.app.name} v{config.app.version}</Box>
            </Typography>

            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{
                fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                color: 'rgba(245,158,11,0.6)',
                fontSize: '0.65rem',
                letterSpacing: '0.3px',
              }}
            >
              Technical Evaluation & Assessment Platform
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AppLayout;

